import { useLayoutEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { type OpportunityStage } from "@forgepoint/shared";
import { cn } from "@/lib/utils";

interface OpportunityStageStepperProps {
  stages: OpportunityStage[];
  currentStage: OpportunityStage | undefined;
}

/**
 * Read-only progress stepper for an opportunity's stage. Labels wrap to fit;
 * when a stage word still can't fit its column, labels collapse into a single
 * current-stage caption under the circles.
 * @param props - Active stages in order and the opportunity's current stage
 * @returns The stepper, or null when there are no stages
 */
export function OpportunityStageStepper({
  stages,
  currentStage,
}: OpportunityStageStepperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  const stageNames = stages.map((s) => s.name).join("\n");

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    // Compact labels stay in the layout so overflow is measurable in both modes.
    const measure = () => {
      const labels =
        container.querySelectorAll<HTMLElement>("[data-stage-label]");
      setIsCompact(
        Array.from(labels).some(
          (label) => label.scrollWidth > label.clientWidth,
        ),
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [stageNames, currentStage?.id]);

  const stepCount = stages.length;
  if (stepCount === 0) {
    return null;
  }

  const currentStageIndex = stages.findIndex((s) => s.id === currentStage?.id);
  const isLost = currentStage?.category === "LOST";
  const halfCol = 100 / (2 * stepCount);
  const progressFraction =
    !isLost && currentStageIndex > 0 ? currentStageIndex / (stepCount - 1) : 0;

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className="absolute top-3.5 h-0.5 rounded-full bg-muted-foreground/15"
        style={{ left: `${halfCol}%`, right: `${halfCol}%` }}
      />
      {progressFraction > 0 && (
        <div
          className="absolute top-3.5 h-0.5 rounded-full bg-primary transition-all duration-500"
          style={{
            left: `${halfCol}%`,
            width: `${(100 - 2 * halfCol) * progressFraction}%`,
          }}
        />
      )}
      <div
        className="relative grid"
        style={{
          gridTemplateColumns: `repeat(${stepCount}, minmax(0, 1fr))`,
        }}
      >
        {stages.map((stage, index) => {
          const isCompleted = !isLost && index < currentStageIndex;
          const isActive = isLost
            ? stage.category === "LOST"
            : index === currentStageIndex;

          return (
            <div
              key={stage.id}
              className={cn(
                "flex min-w-0 flex-col items-center",
                !isCompact && "gap-2",
              )}
            >
              <div
                title={stage.name}
                className={cn(
                  "h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 shrink-0",
                  isCompleted && "bg-primary text-primary-foreground shadow-sm",
                  isActive &&
                    !isLost &&
                    "bg-primary text-primary-foreground shadow-sm ring-[3px] ring-primary/20",
                  isActive &&
                    isLost &&
                    "bg-destructive text-destructive-foreground shadow-sm ring-[3px] ring-destructive/20",
                  !isCompleted &&
                    !isActive &&
                    "border-2 border-muted-foreground/25 text-muted-foreground/60 bg-background",
                )}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5 stroke-[2.5]" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span
                data-stage-label
                className={cn(
                  "max-w-full px-0.5 text-center text-[11px] font-medium leading-tight transition-colors duration-300",
                  isCompact && "h-0 overflow-hidden opacity-0",
                  isCompleted && "text-primary",
                  isActive && !isLost && "text-primary font-semibold",
                  isActive && isLost && "text-destructive font-semibold",
                  !isCompleted && !isActive && "text-muted-foreground",
                )}
              >
                {stage.name}
              </span>
            </div>
          );
        })}
      </div>
      {isCompact && currentStage && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          <span
            className={cn(
              "font-semibold",
              isLost ? "text-destructive" : "text-primary",
            )}
          >
            {currentStage.name}
          </span>
          {currentStageIndex >= 0 &&
            ` · Stage ${currentStageIndex + 1} of ${stepCount}`}
        </p>
      )}
    </div>
  );
}
